import React, { useEffect, useState } from 'react';
import { stockAPI, productAPI, categoryAPI } from '../../services/api';
import { Loader, Plus, Pencil, Trash2 } from 'lucide-react';

const Stocks = () => {
    const [stocks, setStocks] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingStock, setEditingStock] = useState(null);
    const [formData, setFormData] = useState({ product_id: '', product_variation_id: '', quantity: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [stockRes, productRes, categoryRes] = await Promise.all([
                stockAPI.getAll(),
                productAPI.getAllAdmin(),
                categoryAPI.getAllAdmin({})
            ]);
            setStocks(stockRes.data.stock || []);
            setProducts(productRes.data.products || []);
            setCategories(categoryRes.data.categories || []);
        } catch (error) {
            console.error("Failed to fetch data", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('handleSubmit called with formData:', formData);
        try {
            console.log('Calling stockAPI.create');
            await stockAPI.create(formData);
            console.log('API call successful');
            setShowModal(false);
            setEditingStock(null);
            setFormData({ product_id: '', product_variation_id: '', quantity: '' });
            fetchData();
        } catch (error) {
            console.log('API call failed:', error);
            alert('Operation failed');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this stock entry?')) {
            try {
                await stockAPI.delete(id);
                fetchData();
            } catch (error) {
                alert('Failed to delete stock');
            }
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-50 px-6 pb-12">
            <div className="container mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Stock Management</h1>
                    <button
                        onClick={() => { setEditingStock(null); setFormData({ product_id: '', product_variation_id: '', quantity: '' }); setShowModal(true); }}
                        className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <Plus className="mr-2" size={20} />
                        Add Stock
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader className="animate-spin text-green-600" size={48} />
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {stocks.map((stock) => (
                                    <tr key={stock.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {stock.variation?.product?.name || 'Unknown Product'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {stock.variation?.category?.name || 'Unknown Category'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                            {stock.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleDelete(stock.id)}
                                                className="text-red-600 hover:text-red-900 ml-4"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {stocks.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No stock records found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4">Add Stock</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
                                <select
                                    value={formData.product_id}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            product_id: e.target.value,
                                            product_variation_id: '' // Reset variation when product changes
                                        });
                                    }}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                                    required
                                >
                                    <option value="">Select Product</option>
                                    {products.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Show Variations Dropdown if Product Selected */}
                            {formData.product_id && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Variation</label>
                                    <select
                                        value={formData.product_variation_id}
                                        onChange={(e) => setFormData({ ...formData, product_variation_id: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                                        required
                                    >
                                        <option value="">Select Variation</option>
                                        {products.find(p => p.id === parseInt(formData.product_id))?.variations?.map(v => (
                                            <option key={v.id} value={v.id}>
                                                {v.category?.name} - ₹{v.price} ({v.discount}% off)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity (Add)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.quantity}
                                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Stocks;
